import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputBase,
  Menu,
  Stack,
  Typography,
  Tooltip,
  styled,
  FormControl,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import {type ComponentProps, useRef, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import LabelIcon from '@mui/icons-material/LabelOutlined';
import {useTranslation} from 'react-i18next';

const DEFAULT_TRANSLATION_KEYS = {createNewTag: 'createNewTag', startTyping: 'startTyping', addNewLabel: 'addNewLabel'};

export const Tags = ({
  tagsMap,
  selectedTagIds,
  onSelectionChange,
  onCreate,
  translationKeys,
  dataTestIdPrefix,
  helperText,
  label,
  disabled,
  ...rest
}: {
  tagsMap: Map<string, string>;
  selectedTagIds: string[];
  onSelectionChange: (newSelectedTagIds: string[]) => void;
  onCreate: (newTag: string) => void;
  translationKeys?: {
    createNewTag?: string;
    startTyping?: string;
    addNewLabel?: string;
  };
  dataTestIdPrefix?: string;
  label?: string;
  helperText?: string | null;
  disabled?: boolean;
} & ComponentProps<typeof FormControl>) => {
  const {t} = useTranslation('common');
  const translationKeysOverwrites = {...DEFAULT_TRANSLATION_KEYS, ...translationKeys};

  const rootRef = useRef<HTMLDivElement>(null);
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const inputRefElement = useRef<null | HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const tagNamesSet = new Set(tagsMap.values());
  const selectedTagIdsSet = new Set(selectedTagIds);

  const openMenu = () => {
    if (!menuAnchorElement) {
      setMenuAnchorElement(rootRef.current);
    }
  };

  const closeMenu = () => {
    setMenuAnchorElement(null);
    setInputValue('');
  };

  const handleTagSelection = (tagId: string) => {
    const newSelectionSet = new Set(selectedTagIdsSet);
    if (selectedTagIdsSet.has(tagId)) {
      newSelectionSet.delete(tagId);
    } else {
      newSelectionSet.add(tagId);
    }

    onSelectionChange(Array.from(newSelectionSet));
  };

  return (
    <FormControl {...rest}>
      <Stack spacing={1}>
        {label && <FormLabel>{label}</FormLabel>}
        <Stack
          ref={rootRef}
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          data-testid={`${dataTestIdPrefix}tags-root`}
        >
          <Menu
            anchorEl={menuAnchorElement}
            open={!!menuAnchorElement}
            onClose={closeMenu}
            data-testid={`${dataTestIdPrefix}tags-menu-root`}
          >
            <Stack>
              <Box px={2} pb={0.7}>
                <InputBase
                  ref={inputRefElement}
                  placeholder={t(translationKeysOverwrites.startTyping)}
                  value={inputValue}
                  onChange={(event) => {
                    setInputValue(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  autoFocus
                  size="small"
                  data-testid={`${dataTestIdPrefix}tags-menu-input`}
                />
              </Box>
              <Divider />
              <Stack>
                {Array.from(tagsMap.entries())
                  .filter((tag) => tag[1].toLowerCase().startsWith(inputValue.toLowerCase()))
                  .map(([tagId, tagName]) => (
                    <TagMenuItem
                      key={tagId}
                      tagId={tagId}
                      tag={tagName}
                      checked={selectedTagIdsSet.has(tagId)}
                      onCheck={() => {
                        handleTagSelection(tagId);
                      }}
                      onSelect={() => {
                        handleTagSelection(tagId);
                        setMenuAnchorElement(null);
                      }}
                      dataTestIdPrefix={dataTestIdPrefix}
                    />
                  ))}
                {inputValue.length >= 3 && !tagNamesSet.has(inputValue) && (
                  <Button
                    onClick={() => {
                      onCreate(inputValue);
                      setInputValue('');
                    }}
                    startIcon={<AddIcon />}
                    fullWidth
                    color="secondary"
                    data-testid={`${dataTestIdPrefix}tags-submit-creation`}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize="small">{t(translationKeysOverwrites.createNewTag)}</Typography>
                      <Typography fontSize="small" color="primary">
                        {inputValue}
                      </Typography>
                    </Stack>
                  </Button>
                )}
              </Stack>
            </Stack>
          </Menu>
          {selectedTagIds.map((tagId) => (
            <Chip
              onClick={openMenu}
              label={
                <Typography sx={{mx: 0.2}} fontSize="small">
                  {tagsMap.get(tagId)}
                </Typography>
              }
              key={`tag-${tagId}`}
              variant="outlined"
              onDelete={() => {
                handleTagSelection(tagId);
              }}
              size="small"
              disabled={disabled}
            />
          ))}

          {selectedTagIds.length === 0 ? (
            <Button
              onClick={openMenu}
              size="small"
              variant="text"
              color="secondary"
              startIcon={<LabelIcon />}
              data-testid={`${dataTestIdPrefix}add-tag`}
              disabled={disabled}
            >
              {t(translationKeysOverwrites.addNewLabel)}
            </Button>
          ) : (
            !disabled && (
              <Tooltip title={t(translationKeysOverwrites.addNewLabel)}>
                <IconButton
                  onClick={openMenu}
                  size="small"
                  data-testid={`${dataTestIdPrefix}add-tag`}
                  disabled={disabled}
                >
                  <AddIcon fontSize="inherit" color="secondary" />
                </IconButton>
              </Tooltip>
            )
          )}
        </Stack>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Stack>
    </FormControl>
  );
};

const TagMenuItem = ({
  tagId,
  tag,
  checked,
  onCheck,
  onSelect,
  dataTestIdPrefix,
}: {
  tagId: string;
  tag: string;
  checked?: boolean;
  onCheck: () => void;
  onSelect: () => void;
  dataTestIdPrefix?: string;
}) => {
  return (
    <TagMenuItemWrapperStack
      onClick={onSelect}
      direction="row"
      alignItems="center"
      data-testid={`${dataTestIdPrefix}tags-menu-element-${tagId}`}
    >
      <Checkbox
        size="small"
        onClick={(event) => {
          onCheck();
          event.stopPropagation();
        }}
        checked={checked}
        data-testid={`${dataTestIdPrefix}tags-menu-checkbox-${tagId}`}
      />
      <Typography>{tag}</Typography>
    </TagMenuItemWrapperStack>
  );
};

const TagMenuItemWrapperStack = styled(Stack)`
  cursor: pointer;
  :hover {
    background: ${({theme}) => theme.palette.background.default};
  }
`;
